import type { InstructionStep, LifestyleTags, Recipe } from '../types';

export const LIFESTYLE_TAG_KEYS = [
    'isKidFriendly',
    'isQuickPrep',
    'isBudgetFriendly',
] as const satisfies readonly (keyof LifestyleTags)[];

export const LIFESTYLE_TAG_CONFIG: { key: keyof LifestyleTags; labelKey: string; emoji: string }[] = [
    { key: 'isKidFriendly', labelKey: 'isKidFriendly', emoji: '🧸' },
    { key: 'isQuickPrep', labelKey: 'isQuickPrep', emoji: '⚡' },
    { key: 'isBudgetFriendly', labelKey: 'isBudgetFriendly', emoji: '🪙' },
];

export const EMPTY_LIFESTYLE_TAGS: LifestyleTags = {
    isKidFriendly: false,
    isQuickPrep: false,
    isBudgetFriendly: false,
};

export const QUICK_PREP_MAX_MINUTES = 25;

export function normalizeLifestyleTags(value: unknown): LifestyleTags {
    const base = { ...EMPTY_LIFESTYLE_TAGS };
    if (!value || typeof value !== 'object') return base;

    const source = value as Record<string, unknown>;
    for (const key of LIFESTYLE_TAG_KEYS) {
        base[key] = source[key] === true;
    }
    return base;
}

type LifestyleEstimateInput = Pick<Recipe, 'name' | 'prepTime' | 'ingredients' | 'category' | 'instructions' | 'suitableFor'>;

const KID_FRIENDLY_KEYWORDS = [
    'pasta', 'pizza', 'noodle', 'mac and cheese', 'pancake', 'waffle', 'cookie', 'cupcake',
    'nugget', 'meatball', 'burger', 'fries', 'mashed', 'rice', 'corn', 'cheese',
    'פסטה', 'פיצה', 'נודלס', 'פנקייק', 'וaffle', 'עוג', 'ביסקוויט', 'נuggets', 'כדורי',
    'המבורגר', 'צ\'יפס', 'תפוח אדמה', 'אורז', 'תירס', 'גבינ',
];

const KID_UNFRIENDLY_KEYWORDS = [
    'spicy', 'chili', 'hot sauce', 'curry', 'wasabi', 'bitter', 'olive', 'anchovy',
    'wine', 'beer', 'alcohol', 'vodka', 'whiskey', 'brandy', 'oyster', 'sardine',
    'חריף', 'פלפל חריף', 'ווסאבי', 'מר', 'יין', 'אלכוהול', 'וודקה', 'אנשובי',
];

const EXPENSIVE_KEYWORDS = [
    'salmon', 'tuna steak', 'steak', 'ribeye', 'wagyu', 'lobster', 'scallop', 'truffle',
    'saffron', 'duck breast', 'lamb chop', 'filet mignon', 'sea bass', 'caviar',
    'סלמון', 'אנטריקוט', 'לובסטר', 'כבד אווז', 'פילה בקר', 'כבש', 'טroot', 'זעפרן',
];

const BUDGET_KEYWORDS = [
    'rice', 'pasta', 'bean', 'lentil', 'chickpea', 'egg', 'potato', 'onion', 'carrot',
    'canned', 'frozen', 'bread', 'oat', 'flour', 'tomato sauce',
    'אורז', 'פסטה', 'שעועית', 'עדשים', 'חומוס', 'ביצ', 'תפוח אדמה', 'בצל', 'גזר', 'קמח',
];

function instructionStepCount(instructions: string | InstructionStep[] | undefined): number {
    if (!instructions) return 0;
    if (typeof instructions === 'string') {
        return instructions.split('\n').map((line) => line.trim()).filter(Boolean).length;
    }
    return instructions.filter((step) => step.type === 'step').length;
}

function collectRecipeText(recipe: LifestyleEstimateInput): string {
    const ingredientText = recipe.ingredients.map((ing) => ing.name).join(' ');
    let instructionText = '';
    if (typeof recipe.instructions === 'string') {
        instructionText = recipe.instructions;
    } else if (Array.isArray(recipe.instructions)) {
        instructionText = recipe.instructions.map((step) => step.content).join(' ');
    }
    return `${recipe.name} ${ingredientText} ${instructionText}`.toLowerCase();
}

function includesKeyword(text: string, keywords: readonly string[]): boolean {
    return keywords.some((keyword) => text.includes(keyword.toLowerCase()));
}

function estimateKidFriendly(recipe: LifestyleEstimateInput, text: string): boolean {
    if (recipe.category === 'Dessert' || recipe.category === 'Snack') return true;

    if (includesKeyword(text, KID_UNFRIENDLY_KEYWORDS)) return false;
    if (includesKeyword(text, KID_FRIENDLY_KEYWORDS)) return true;

    const ingredientCount = recipe.ingredients.length;
    const stepCount = instructionStepCount(recipe.instructions);
    return ingredientCount <= 6 && stepCount <= 5;
}

function estimateBudgetFriendly(recipe: LifestyleEstimateInput, text: string): boolean {
    if (includesKeyword(text, EXPENSIVE_KEYWORDS)) return false;

    const budgetHits = BUDGET_KEYWORDS.filter((keyword) => text.includes(keyword.toLowerCase())).length;
    if (budgetHits >= 2) return true;

    const premiumMeatCount = recipe.ingredients.filter((ing) => {
        if (!ing.isMeat) return false;
        const name = ing.name.toLowerCase();
        return EXPENSIVE_KEYWORDS.some((keyword) => name.includes(keyword.toLowerCase()));
    }).length;

    if (premiumMeatCount > 0) return false;
    return recipe.ingredients.length <= 10;
}

export function estimateLifestyleTags(recipe: LifestyleEstimateInput): LifestyleTags {
    const text = collectRecipeText(recipe);
    const prepTime = recipe.prepTime ?? 20;

    return {
        isQuickPrep: prepTime <= QUICK_PREP_MAX_MINUTES,
        isKidFriendly: estimateKidFriendly(recipe, text),
        isBudgetFriendly: estimateBudgetFriendly(recipe, text),
    };
}

export function mergeEstimatedLifestyleTags(
    existing: LifestyleTags,
    estimates: LifestyleTags,
): LifestyleTags {
    return {
        isQuickPrep: existing.isQuickPrep || estimates.isQuickPrep,
        isKidFriendly: existing.isKidFriendly || estimates.isKidFriendly,
        isBudgetFriendly: existing.isBudgetFriendly || estimates.isBudgetFriendly,
    };
}

export function recipeHasLifestyleTag(
    recipe: Pick<Recipe, 'lifestyleTags' | 'prepTime' | 'ingredients' | 'category' | 'instructions' | 'name' | 'suitableFor'>,
    key: keyof LifestyleTags,
): boolean {
    const stored = normalizeLifestyleTags(recipe.lifestyleTags);
    if (stored[key]) return true;

    const estimated = estimateLifestyleTags(recipe);
    return estimated[key];
}
